import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";

import useTranslations from "@/i18n/useTranslations";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import XIcon from "@/shared-components/icons/components/public/XIcon";
import GenericPopup from "@/shared-components/popups/components/GenericPopup";

import "./styles/PurchasePaymentModal.css";

const PAYMENT_ELEMENT_OPTIONS = {
  layout: "tabs",
  paymentMethodOrder: ["card"],
  wallets: {
    applePay: "never",
    googlePay: "never",
    link: "never",
  },
  fields: {
    billingDetails: {
      name: "auto",
      email: "auto",
      phone: "auto",
      address: "if_required",
    },
  },
};

function PurchasePaymentForm({ onComplete, submitLabel }) {
  const t = useTranslations("common.shared.artwork_view.payment_profile_modal");
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) {
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        throw submitResult.error;
      }

      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });
      if (result?.error) {
        throw result.error;
      }

      const paymentIntent = result?.paymentIntent ?? null;
      if (!paymentIntent || !["succeeded", "processing", "requires_capture"].includes(paymentIntent.status)) {
        throw new Error(t("errors.unable_to_verify"));
      }
      await onComplete(paymentIntent);
    }
    catch (submitError) {
      setError(submitError?.message ?? t("errors.unable_to_verify"));
    }
    finally {
      setSubmitting(false);
    }
  }, [elements, onComplete, stripe, submitting, t]);

  return (
    <form className="purchase-payment-modal__form" onSubmit={handleSubmit}>
      <div className="purchase-payment-modal__payment-wrapper">
        <PaymentElement
          id="purchase-payment-element"
          className="purchase-payment-modal__payment-element"
          options={PAYMENT_ELEMENT_OPTIONS}
        />
      </div>

      {error && <p className="purchase-payment-modal__error" role="alert">{error}</p>}

      <div className="purchase-payment-modal__actions">
        <ContinueButton
          type="submit"
          className="purchase-payment-modal__submit"
          disabled={submitting || !stripe || !elements}
          loading={submitting}
        >
          {submitLabel}
        </ContinueButton>
      </div>
    </form>
  );
}

PurchasePaymentForm.propTypes = {
  onComplete: PropTypes.func.isRequired,
  submitLabel: PropTypes.string.isRequired,
};

export default function PurchasePaymentModal({ clientSecret, onCancel, onComplete, stripe, submitLabel, title }) {
  const options = useMemo(() => ({ clientSecret }), [clientSecret]);

  if (!clientSecret || !stripe) {
    return null;
  }

  return (
    <GenericPopup
      open
      className="purchase-payment-modal__container"
      backgroundClassName="purchase-payment-modal__overlay"
    >
      <header className="purchase-payment-modal__header">
        <div className="purchase-payment-modal__title-row">
          <h2 className="purchase-payment-modal__title">{title}</h2>
          <XIcon className="generic-popup__close" onClick={onCancel} />
        </div>
      </header>

      <Elements stripe={stripe} options={options}>
        <PurchasePaymentForm onComplete={onComplete} submitLabel={submitLabel} />
      </Elements>
    </GenericPopup>
  );
}

PurchasePaymentModal.propTypes = {
  clientSecret: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  stripe: PropTypes.oneOfType([PropTypes.object, PropTypes.instanceOf(Promise)]),
  submitLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
